import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'
import DOMPurify from 'dompurify'
import htmldiff from 'node-htmldiff'
import { Badge, Button, Icon, InfoBox, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, TanTable, TanTableColumnDef } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { roles as roleOptions } from '@evertel/constants'
import { AgreementController, AgreementTimelineEntry } from '@evertel/agreements'
import { AgreementTextEditor } from '../elements/AgreementTextEditor'
import { sanitizeAgreementHtml } from '../../utils/sanitize-agreement-html'

interface AgreementVersionHistoryModalProps {
    visible: boolean
    agreementId: number
    departmentId: number
    agreementTitle: string
    currentDocumentId?: number
    onClose: () => void
}

const AgreementVersionHistoryModal: React.FC<AgreementVersionHistoryModalProps> = observer(({
    visible,
    agreementId,
    departmentId,
    agreementTitle,
    currentDocumentId,
    onClose
}) => {
    const agreementController = useService(AgreementController, [])
    const [selectedEntry, setSelectedEntry] = useState<AgreementTimelineEntry | null>(null)
    const [viewMode, setViewMode] = useState<'changes' | 'content'>('content')

    useEffect(() => {
        if (visible && agreementId) {
            // This component gets its own (transient) controller instance, so
            // init here or fetchRoomNames has no departmentId to query with.
            agreementController.init(departmentId)
            agreementController.resetHistory()
            agreementController.fetchDocumentHistory(agreementId)
            agreementController.fetchSettingsHistory(agreementId)
        }
    }, [visible, agreementId, departmentId])

    useEffect(() => {
        if (!visible) setSelectedEntry(null)
    }, [visible])

    // Lazily resolve room names when a settings entry with a rooms change is
    // opened. One deduped fetch per entry, covering both from and to lists;
    // names already known (RoomStore or prior fetches) are skipped.
    useEffect(() => {
        if (selectedEntry?.type !== 'settings') return
        const roomIdsChange = selectedEntry.history.data?.changeSet?.roomIds
        if (!roomIdsChange) return

        const ids = [
            ...((roomIdsChange.from as number[]) ?? []),
            ...((roomIdsChange.to as number[]) ?? [])
        ]
        if (ids.length) {
            agreementController.fetchRoomNames(ids)
        }
    }, [selectedEntry])

    const timeline = agreementController.timeline

    // Immediately previous content version = highest version below the selected
    // one. Only meaningful for content entries (settings have no version).
    const previousDoc = useMemo(() => {
        if (selectedEntry?.type !== 'content') return null
        const selectedVersionNumber = selectedEntry.document.version
        if (selectedVersionNumber == null) return null
        return agreementController.documents
            .filter(d => (d.version ?? 0) < selectedVersionNumber)
            .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0] ?? null
    }, [selectedEntry, agreementController.documents])

    // Open on the plain content whenever a different entry is selected.
    useEffect(() => {
        setViewMode('content')
    }, [selectedEntry])

    // Normalize both sides first so BlockNote serialization noise doesn't show as edits.
    const diffHtml = useMemo(() => {
        if (selectedEntry?.type !== 'content' || !previousDoc) return null
        const a = DOMPurify.sanitize(previousDoc.content ?? '')
        const b = DOMPurify.sanitize(selectedEntry.document.content ?? '')
        return sanitizeAgreementHtml(htmldiff(a, b), { ADD_TAGS: ['ins', 'del'] })
    }, [selectedEntry, previousDoc])

    const columns = useMemo<TanTableColumnDef<AgreementTimelineEntry>[]>(() => [
        {
            id: 'createdBy',
            header: 'Created By',
            // size: '35%',
            cell: ({ row }) => {
                const entry = row.original
                const user = entry.type === 'content' ? entry.document.createdBy : entry.history.createdBy
                return displayName(user)
            }
        },
        {
            id: 'createdDate',
            header: 'Created',
            // size: '25%',
            cell: ({ row }) => row.original.createdDate
                ? moment(row.original.createdDate).format('MM/DD/YYYY')
                : '—'
        },
        {
            id: 'change',
            header: 'Change',
            // size: '10%',
            cell: ({ row }) => {
                const entry = row.original
                if (entry.type === 'content') {
                    const isCurrent = entry.document.id === currentDocumentId
                    return (
                        <Badge
                            variant={isCurrent ? 'soft' : 'solid'}
                            color={isCurrent ? 'primary' : 'secondary'}
                        >
                            v{entry.document.version}
                        </Badge>
                    )
                }
                const keys = entry.history.data?.changeSet ? Object.keys(entry.history.data.changeSet) : []
                return (
                    <span className="">
                        <Badge color="info" variant="soft" className="me-2">Settings</Badge>

                        {keys.length ? (
                            <>
                                <br />
                                <span className="text-muted small">
                                    <strong>Changed:</strong> {keys.map(key => SETTING_FIELD_LABELS[key] ?? key).join(', ')}
                                </span>
                            </>
                        ) : null}
                    </span>
                )
            }
        },
        {
            id: 'chevron',
            header: '',
            size: 32,
            cell: () => <Icon name="chevron-right" size="xs" />
        }
    ], [currentDocumentId])

    const renderSettingsDetail = (entry: Extract<AgreementTimelineEntry, { type: 'settings' }>) => {
        const changeSet = entry.history.data?.changeSet ?? {}
        const keys = Object.keys(changeSet)

        if (!keys.length) {
            return <InfoBox color="info">No field changes were recorded for this event.</InfoBox>
        }

        return (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {keys.map((key) => {
                    const { from, to } = changeSet[key]

                    let content: React.ReactNode
                    if (key === 'restrictedRoles') {
                        content = (
                            <>
                                <div className="mb-2">
                                    <span className="text-muted small d-block mb-1">From</span>
                                    {renderValueBadges(normalizeRolesForDisplay((from as string[]) ?? []), 'All roles', roleLabel, roleColor)}
                                </div>
                                <div>
                                    <span className="text-muted small d-block mb-1">To</span>
                                    {renderValueBadges(normalizeRolesForDisplay((to as string[]) ?? []), 'All roles', roleLabel, roleColor)}
                                </div>
                            </>
                        )
                    } else if (key === 'roomIds') {
                        content = (
                            <>
                                <div className="mb-2">
                                    <span className="text-muted small d-block mb-1">From</span>
                                    {renderValueBadges((from as number[]) ?? [], 'All rooms', agreementController.roomLabel)}
                                </div>
                                <div>
                                    <span className="text-muted small d-block mb-1">To</span>
                                    {renderValueBadges((to as number[]) ?? [], 'All rooms', agreementController.roomLabel)}
                                </div>
                            </>
                        )
                    } else if (key === 'enabled') {
                        content = renderBooleanPhrase(to as boolean, 'Enabled', 'Disabled', 'success', 'danger')
                    } else if (key === 'acceptOncePerRoom') {
                        content = renderBooleanPhrase(to as boolean, 'On', 'Off', 'success', 'danger')
                    } else {
                        content = (
                            <>
                                <div><strong>From:</strong> {formatSettingValue(from)}</div>
                                <div><strong>To:</strong> {formatSettingValue(to)}</div>
                            </>
                        )
                    }

                    return (
                        <div key={key} className="mb-3 p-3 rounded" style={{ backgroundColor: 'var(--bs-agreement-bg)', wordWrap: 'break-word' }}>
                            <strong className="d-block mb-2">{SETTING_FIELD_LABELS[key] ?? key}</strong>
                            {content}
                        </div>
                    )
                })}
            </div>
        )
    }

    const headerBadge = selectedEntry?.type === 'content'
        ? (
            <Badge
                color={selectedEntry.document.id === currentDocumentId ? 'primary' : 'secondary'}
                variant={selectedEntry.document.id === currentDocumentId ? 'soft' : 'solid'}
            >v{selectedEntry.document.version}
            </Badge>
        )
        : selectedEntry?.type === 'settings'
            ? <Badge color="info" variant="soft">Settings</Badge>
            : null

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="lg"
        >
            <ModalHeader
                title={
                    selectedEntry ? (
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                color="link"
                                className="p-0 me-1"
                                onClick={() => setSelectedEntry(null)}
                            >
                                <Icon name="arrow-left" type="solid" />
                            </Button>
                            {agreementTitle}
                            {headerBadge}
                        </div>
                    ) : 'Version History'
                }
                subtitle={selectedEntry ? undefined : agreementTitle}
            />
            <ModalBody>
                {selectedEntry?.type === 'content' ? (
                    <>
                        {previousDoc && (
                            <Nav variant="underline" role="tablist" className="mb-3">
                                <NavItem>
                                    <NavLink
                                        active={viewMode === 'content'}
                                        onClick={() => setViewMode('content')}
                                    >
                                        Content
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        active={viewMode === 'changes'}
                                        onClick={() => setViewMode('changes')}
                                    >
                                        Changes from previous version
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        )}
                        {viewMode === 'changes' && diffHtml !== null ? (
                            <div
                                className="agreement-diff border rounded p-3"
                                style={{ backgroundColor: 'var(--bs-agreement-bg)', maxHeight: 500, overflowY: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: diffHtml }}
                            />
                        ) : (
                            <AgreementTextEditor
                                value={selectedEntry.document.content ?? ''}
                                editable={false}
                                minHeight={100}
                                maxHeight={500}
                            />
                        )}
                        <div className="d-flex justify-content-end mt-3">
                            <Button color="primary" onClick={onClose}>Close</Button>
                        </div>
                    </>
                ) : selectedEntry?.type === 'settings' ? (
                    <>
                        {renderSettingsDetail(selectedEntry)}
                        <div className="d-flex justify-content-end mt-3">
                            <Button color="primary" onClick={onClose}>Close</Button>
                        </div>
                    </>
                ) : (
                    <>
                        <TanTable
                            data={timeline}
                            columns={columns}
                            options={{
                                pageSize: 10,
                                pageNumber: 1,
                                showPagination: timeline.length > 10,
                                showTotal: true
                            }}
                            onRowClick={(row) => setSelectedEntry(row.original)}
                        />
                        <div className="d-flex justify-content-end mt-3">
                            <Button color="primary" onClick={onClose}>Close</Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    )
})

// Fallback formatter for any changeSet field without dedicated rendering below.
// roomIds/restrictedRoles/enabled/acceptOncePerRoom - the only fields
// updateSettings can send - all have dedicated rendering in renderSettingsDetail.
const formatSettingValue = (value: unknown) => {
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
    }
    return JSON.stringify(value)
}

const SETTING_FIELD_LABELS: Record<string, string> = {
    roomIds: 'Rooms',
    restrictedRoles: 'Restricted Roles',
    enabled: 'Status',
    acceptOncePerRoom: 'Accept Once Per Room'
}

const roleLabel = (value: string) => roleOptions.find(r => r.value === value)?.label ?? value

// Matches the role color convention already duplicated across DeptUsers.tsx,
// AdminUsers.tsx and ManageDepartmentUsers.tsx.
const roleColor = (value: string) => {
    if (value === 'management') return 'success'
    if (value === 'guest') return 'warning'
    if (value === 'executive') return 'danger'
    return 'info'
}

// restrictedRoles has no backend normalization: manually selecting all 4
// roles saves the literal array instead of []. Both mean "no restriction",
// so collapse either form to the empty-array "All Roles" display path.
const sortedRoles = (values: string[]) =>
    [...values].sort((a, b) => roleOptions.findIndex(r => r.value === a) - roleOptions.findIndex(r => r.value === b))

const normalizeRolesForDisplay = (values: string[]) =>
    values.length === roleOptions.length && roleOptions.every(r => values.includes(r.value)) ? [] : sortedRoles(values)

const renderValueBadges = (
    values: unknown[], emptyLabel: string,
    toLabel: (v: any) => string = String,
    toColor: (v: any) => string = () => 'muted'
) => {
    if (!values.length) {
        return <Badge color="primary">{emptyLabel}</Badge>
    }
    return (
        <div className="d-flex flex-wrap gap-1">
            {values.map((v, i) => <Badge key={i} color={toColor(v)}>{toLabel(v)}</Badge>)}
        </div>
    )
}

const renderBooleanPhrase = (
    to: boolean,
    trueLabel: string, falseLabel: string,
    trueColor: string, falseColor: string
) => (
    <span>
        Agreement changed to <strong className={`text-${to ? trueColor : falseColor}`}>{to ? trueLabel : falseLabel}</strong>
    </span>
)

const displayName = (user?: { firstName?: string, lastName?: string, email?: string } | null) => {
    if (!user) return '—'
    return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '—'
}

AgreementVersionHistoryModal.displayName = 'AgreementVersionHistoryModal'

export { AgreementVersionHistoryModal }
